import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Plane, RefreshCw, AlertTriangle, TrendingUp, Users, CheckCircle, Clock,
  ArrowLeft, Activity
} from 'lucide-react';
import FavoriteStar from '@/components/FavoriteStar';
import AppLogo from '@/components/AppLogo';
import DisruptionBoard from '@/components/DisruptionBoard';
import SEOHead, { buildBreadcrumb } from '@/components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const LEVEL_META = {
  low:     { label: 'Low',     color: '#3b82f6', bg: 'bg-blue-50',   text: 'text-blue-600',   border: 'border-blue-100',   Icon: CheckCircle },
  medium:  { label: 'Medium',  color: '#6366f1', bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-100', Icon: Users },
  high:    { label: 'High',    color: '#8b5cf6', bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-100', Icon: TrendingUp },
  extreme: { label: 'Extreme', color: '#a855f7', bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-100', Icon: AlertTriangle },
};

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const min = Math.floor(diffSec / 60);
  if (min < 60) return `${min} min ago`;
  const hrs = Math.floor(min / 60);
  return `${hrs}h ago`;
}

export default function BusiestNowPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/airport/busiest-now`);
      if (res.ok) {
        setData(await res.json());
      } else {
        setError('Failed to load live data. Please try again.');
      }
    } catch (e) {
      console.error(e);
      setError('Failed to load live data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    document.title = 'Busiest US Airports Right Now — Live Crowd Levels';
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead
        title="Busiest US Airports Right Now — Live Crowd Levels"
        description="Live crowd-level snapshot across 16 major US airport hubs (ATL, LAX, ORD, DFW, JFK, DEN, SFO, SEA, LAS, MCO and more). Updated every 10 minutes from live flight schedules."
        path="/live"
        keywords="busiest airports right now, live airport crowds, current TSA wait times, airport traffic now, real time airport delays"
        structuredData={[
          buildBreadcrumb([
            { name: 'Home', url: '/' },
            { name: 'Live Busiest', url: '/live' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Busiest US Airports Right Now',
            description: 'Real-time crowd levels for the 16 busiest US airport hubs.',
            url: 'https://howbusyismyairport.com/live',
          },
        ]}
      />
      <header className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 max-w-6xl flex items-center justify-between gap-3">
          <Link to="/" className="inline-flex items-center gap-2 sm:gap-2.5 text-slate-700 hover:text-slate-900 transition-colors min-w-0" data-testid="back-home">
            <AppLogo size={28} className="flex-shrink-0 sm:[&>svg]:w-8 sm:[&>svg]:h-8" />
            <div className="leading-tight min-w-0">
              <div className="text-[11px] sm:text-sm font-semibold leading-[1.15]">How Busy Is My Airport</div>
              <div className="text-[10px] sm:text-[11px] text-slate-400 -mt-0.5 inline-flex items-center gap-1">
                <ArrowLeft className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> Back home
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs text-slate-500 flex-shrink-0">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-rose-400 opacity-75 animate-ping"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500"></span>
            </span>
            <span className="hidden xs:inline sm:inline">Live snapshot</span>
            <span className="xs:hidden sm:hidden">Live</span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-8 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          className="mb-8"
        >
          <h1 className="heading text-3xl sm:text-4xl font-bold text-slate-800 mb-2">
            Busiest US Airports Right Now
          </h1>
          <p className="text-slate-500 max-w-2xl">
            Live crowd-level snapshot across major US hubs, updated every 10 minutes from FlightAware schedules.
            Tap any airport to dig deeper.
          </p>
        </motion.div>

        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500" data-testid="updated-at">
            {data?.updated_at ? `Updated ${formatRelative(data.updated_at)}${data.cached ? ' (cached)' : ''}` : ''}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="refresh-btn"
            className="border-slate-300 text-slate-600 hover:bg-slate-100"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <Card className="border-rose-200 bg-rose-50 mb-6">
            <CardContent className="p-4 text-rose-600 text-sm">{error}</CardContent>
          </Card>
        )}

        {loading && !data ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 9 }).map((_, i) => (
              <Card key={i} className="border-slate-200 bg-white">
                <CardContent className="p-5">
                  <div className="h-24 bg-slate-100 rounded animate-pulse" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : data?.airports?.length === 0 ? (
          <Card className="border-slate-200 bg-white">
            <CardContent className="p-8 text-center text-slate-500">
              <Plane className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              No live data available right now. The flight data API may be rate-limited — try again in a few minutes.
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(data?.airports || []).map((a, idx) => {
              const meta = LEVEL_META[a.crowd_level] || LEVEL_META.medium;
              const Icon = meta.Icon;
              return (
                <motion.div
                  key={a.code}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: idx * 0.03 }}
                >
                  <Link
                    to={`/?airport=${a.code}`}
                    data-testid={`busiest-card-${a.code}`}
                    className="block"
                  >
                    <Card className={`border ${meta.border} bg-white hover:shadow-md transition-shadow`}>
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-400">#{idx + 1}</span>
                            <span className="font-mono text-xl font-bold text-slate-800">{a.code}</span>
                            <FavoriteStar code={a.code} size={16} />
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${meta.bg} ${meta.text} flex items-center gap-1`}>
                            <Icon className="w-3 h-3" />
                            {meta.label}
                          </span>
                        </div>

                        <div className="text-sm text-slate-700 font-medium mb-1 truncate">
                          {a.city}{a.state ? `, ${a.state}` : ''}
                        </div>
                        <div className="text-xs text-slate-400 truncate mb-4">{a.name}</div>

                        {a.delay_color && a.delay_color !== 'green' && (
                          <div
                            data-testid={`delay-chip-${a.code}`}
                            title={a.delay_reason || undefined}
                            className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full mb-3 ${
                              a.delay_color === 'red' ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-700'
                            }`}
                          >
                            <AlertTriangle className="w-3 h-3" />
                            FAA delays — avg {Math.round((a.delay_secs || 0) / 60)} min
                          </div>
                        )}

                        <div className="flex items-center justify-between text-xs text-slate-500 pt-3 border-t border-slate-100">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {a.local_time} {a.timezone_label}
                          </span>
                          <span className="font-mono">
                            <span className="text-slate-700 font-semibold">{a.departures_count}</span>
                            <span className="text-slate-400"> dep / </span>
                            <span className="text-slate-700 font-semibold">{a.arrivals_count}</span>
                            <span className="text-slate-400"> arr</span>
                          </span>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Live disruption leaderboard (single-query, 10-min cache) */}
        <DisruptionBoard />

        {/* Static explainer — always rendered for crawlers and first-time visitors */}
        <section className="mt-12 max-w-3xl mx-auto text-left" data-testid="live-explainer">
          <h2 className="heading text-xl sm:text-2xl font-semibold text-slate-900 mb-3">How to read this board</h2>
          <p className="text-sm sm:text-[15px] text-slate-600 leading-relaxed mb-4">
            Each card is a live snapshot of one of the 16 busiest US airport hubs, ranked from most to least crowded
            right now. The crowd level blends the number of departures and arrivals scheduled in the current window,
            the size of the aircraft flying them (a widebody pushes far more passengers through a checkpoint than a
            regional jet), and calibration from real traveler feedback. Departure and arrival counts show the raw
            flight volume behind each score, and the local time tells you where each airport sits in its daily cycle.
          </p>
          <h2 className="heading text-lg sm:text-xl font-semibold text-slate-900 mb-2">Why the ranking changes through the day</h2>
          <p className="text-sm sm:text-[15px] text-slate-600 leading-relaxed mb-4">
            Airports don't get busy at random — they surge in scheduled departure banks. East Coast hubs like{' '}
            <Link to="/airports/atl" className="text-blue-600 hover:underline">ATL</Link> and{' '}
            <Link to="/airports/clt" className="text-blue-600 hover:underline">CLT</Link> dominate this board in the
            early morning (their 7–10 AM bank starts while the West Coast sleeps), then hand the top spots to{' '}
            <Link to="/airports/lax" className="text-blue-600 hover:underline">LAX</Link>,{' '}
            <Link to="/airports/den" className="text-blue-600 hover:underline">DEN</Link> and{' '}
            <Link to="/airports/phx" className="text-blue-600 hover:underline">PHX</Link> as the day rolls west.
            By evening, international gateways like <Link to="/airports/jfk" className="text-blue-600 hover:underline">JFK</Link>{' '}
            climb back up as the transatlantic bank pushes out between 5 and 9 PM local.
          </p>
          <p className="text-sm sm:text-[15px] text-slate-600 leading-relaxed">
            Flying today? Tap your airport's card to run a{' '}
            <Link to="/" className="text-blue-600 hover:underline">personalized arrival-time prediction</Link> for your
            exact flight time, or explore the typical weekly pattern for any hub on the{' '}
            <Link to="/insights" className="text-blue-600 hover:underline">national insights page</Link> and in our{' '}
            <Link to="/airports" className="text-blue-600 hover:underline">airport guides</Link>.
          </p>
        </section>

        <div className="mt-10 text-xs text-slate-400 text-center max-w-2xl mx-auto">
          Crowd scores combine departure/arrival volume, aircraft size weights and recent user feedback for each airport.
          Snapshots are cached to respect FlightAware rate limits.
        </div>
      </main>
    </div>
  );
}
